import { formatearCOP, formatearFecha } from '@tanteo/compartido';
import type { AlcancePromocion } from '@tanteo/compartido';
import type { DescuentoDelNegocio } from '../../lib/api-descuentos';

/** Cómo se lee un descuento en la lista de Configuración y en los avisos de su ventana. */

const DIAS_CORTOS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const DIAS_LARGOS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábados', 'Domingos'];

export function valorDe(d: Pick<DescuentoDelNegocio, 'forma' | 'valor' | 'enProducto' | 'lleva' | 'paga'>): string {
  if (d.forma === 'porcentaje') return `${d.valor}%`;
  if (d.forma === 'monto_fijo') return `−${formatearCOP(d.valor ?? 0)} ${d.enProducto ? 'por unidad' : 'al total'}`;
  if (d.forma === 'lleva_paga') return `${d.lleva}x${d.paga}`;
  return 'Se escribe en el mostrador';
}

/** «Martes», «Vie–Dom» o «Lun, Mié, Vie»; vacío si son todos. */
export function diasDe(dias: number[]): string {
  const orden = [...dias].sort((a, b) => a - b);
  if (orden.length === 0 || orden.length === 7) return '';
  if (orden.length === 1) return DIAS_LARGOS[orden[0]! - 1]!;
  const seguidos = orden.every((d, i) => i === 0 || d === orden[i - 1]! + 1);
  if (seguidos && orden.length > 2) return `${DIAS_CORTOS[orden[0]! - 1]}–${DIAS_CORTOS[orden.at(-1)! - 1]}`;
  return orden.map((d) => DIAS_CORTOS[d - 1]).join(', ');
}

function fechaCorta(fecha: string): string {
  return formatearFecha(fecha).slice(0, 5);
}

export function fechasDe(d: Pick<DescuentoDelNegocio, 'desde' | 'hasta'>): string {
  if (d.desde && d.hasta) return `del ${fechaCorta(d.desde)} al ${fechaCorta(d.hasta)}`;
  if (d.desde) return `desde el ${fechaCorta(d.desde)}`;
  if (d.hasta) return `hasta el ${fechaCorta(d.hasta)}`;
  return '';
}

/** «Siempre» para los manuales; días, fechas y límite para las promociones. */
export function vigenciaDe(d: DescuentoDelNegocio): string {
  const limite =
    d.maxVeces != null ? `máx ${d.maxVeces} ${d.maxVeces === 1 ? 'vez' : 'veces'}/venta` : d.maxUnidades != null ? `máx ${d.maxUnidades} unid./venta` : '';
  const partes = d.automatico ? [diasDe(d.dias), fechasDe(d)] : ['Siempre'];
  return [...partes, limite].filter(Boolean).join(' · ');
}

export function alcanceDe(alcance: AlcancePromocion[], nombreDe: (productoId: string) => string): string {
  return alcance.map((a) => a.categoria ?? (a.productoId ? nombreDe(a.productoId) : '')).join(', ');
}

type EstadoDescuento = 'activo' | 'programado' | 'vencido' | 'inactivo';

export function estadoDe(d: DescuentoDelNegocio, hoy: string): EstadoDescuento {
  if (!d.activo) return 'inactivo';
  if (d.automatico && d.hasta !== null && d.hasta < hoy) return 'vencido';
  if (d.automatico && d.desde !== null && d.desde > hoy) return 'programado';
  return 'activo';
}
